import { ActionIcon, Button, LoadingOverlay, NativeSelect, NumberInput, Tabs, Textarea } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useClickOutside, useDisclosure } from '@mantine/hooks';
import type { TestType } from '@prisma/client';
import { IconCornerDownLeft, IconForms, IconSparkles } from '@tabler/icons-react';
import clsx from 'clsx';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { z } from 'zod';
import { isActionMethod } from '~/ai_tools/playwright_utils/types';
import { browserClickPositionAtom } from '~/atoms/atoms';
import { currentEnvironmentAtom } from '~/atoms/no-code-atoms';
import type { RecorderInitiator } from '~/atoms/recorder-atoms';
import {
  contextMenuDropDownAtom,
  recorderClickModeAtom,
  recorderTabsOpenAtom,
  recordTypeAtom,
} from '~/atoms/recorder-atoms';
import {
  isSwipeDirection,
  zPosition,
  zScrollOperationValue,
  type SwipeDirection,
  type zSwipe,
} from '~/common/recorder/recorder-operation';
import { capFirst } from '~/common/string/capitalize';
import type { TabUpdateMessage, VmFillInputPayload, VmSelectOptionsPayload } from '~/common/vm/protocol';
import { safeParse } from '~/common/zod/safeParse';
import type { RecorderOperation } from '~/components/common/BrowserContextMenu';
import BrowserContextMenu from '~/components/common/BrowserContextMenu';
import OverOverlay from '~/components/common/OverOverlay';
import { MonacoInputField } from '~/components/forms/input/MonacoInputField';
import BrowserLoader from '~/components/loader/BrowserLoader';
import { ParameterizedInput } from '~/components/test/steps/step/operation/ParameterizedInput';
import { ParameterizedTextInputArea } from '~/components/test/steps/step/operation/ParameterizedTextInputArea';
import { AIAssertionConfig } from '~/components/test/steps/step/operation/complex/aiAssertion/AIAssertionConfig';
import { TOUR_ID } from '~/components/tours/DraftEditorTour';
import { useProjectId } from '~/hooks/context/useProjectId';
import useHandleVmStatus from '~/hooks/useHandleVmStatus';
import { useVm } from '~/hooks/vm/useVm';
import { getDeviceViewport } from '~/server/logic/driver-common/devices/devices';
import type { AIAssertionScreenshotType, ValueType } from '~/server/logic/steps/operation/types';
import { type AIAssertionModel } from '~/server/logic/steps/operation/types';
import { withUrlParams } from '~/server/logic/url/addParams';
import DragToOverlay from '../../common/DragToOverlay';
import Browser from '../Browser';
import { FileChooser } from './FileChooser';
import KeyCaptureInput from './KeyCaptureInput';
import { ScrollInput } from './ScrollInput';
import { SwipeDirectionInput } from './SwipeDirectionInput';
import { useTestVariables } from '~/hooks/test/useTestVariables';
import { useLiveStateManager } from '~/hooks/useLiveStateManager';
import { DEFAULT_DELAY_MS } from '~/common/recorder/constants';

// We add a disabled option to the front-end

export const WEB_RTC_OVERLAY_ZIndex = 200;

export type Coordinate = { absolute: number; ratio: number };

const DEFAULT_SCROLL_VALUE = { direction: 'down', amount: 100 } satisfies z.output<typeof zScrollOperationValue>;

const getMouseEventPos = (e: React.MouseEvent<HTMLElement>) => {
  const rect = e.currentTarget.getBoundingClientRect();
  const x = e.clientX - rect.left;
  const y = e.clientY - rect.top;

  const xRatio = x / rect.width;
  const yRatio = y / rect.height;

  return {
    x: { absolute: x, ratio: xRatio },
    y: { absolute: y, ratio: yRatio },
  };
};

// Grouped into one state var because these values are highly coupled
// and updating one often requires the latest version of the other -
// grouping like this prevents stale state values from being used
type TabState = {
  aliases: string[];
  active: string;
};

export default function WebRtcVmView({
  testContentId,
  recorderInitiator,
  testType,
}: {
  testContentId: string;
  recorderInitiator: RecorderInitiator;
  testType: TestType;
}) {
  const router = useRouter();
  const antiBot = router.query.antiBot === 'true' || router.query.antibot === 'true';
  const useProxy = router.query.proxy === 'true';

  const startEpoch = useRef(Date.now());
  const [isPageLevelScroll, setIsPageLevelScroll] = useState(false);

  const actionValueForm = useForm<{
    // Kind of a hack, but we squeeze numbers and strings together here (wait input does numbers)
    // Then we cast back between with String() and Number()
    value: string;
    valueType: ValueType;
    screenshotType: AIAssertionScreenshotType;
    includeScreenshot: boolean;
    model: AIAssertionModel;
    delay: number;
  }>({
    initialValues: {
      value: '',
      valueType: 'STATIC',
      screenshotType: 'VIEWPORT',
      includeScreenshot: true,
      model: 'STABLY_FLASH',
      delay: DEFAULT_DELAY_MS,
    },
  });

  const onParamterizedValueChange = useCallback(
    (value: string, valueType: ValueType) => {
      actionValueForm.setValues({ value, valueType });
    },
    [actionValueForm],
  );

  const videoRef = useRef<HTMLVideoElement>(null);

  const [tabState, setTabState] = useState<TabState>({ aliases: ['page'], active: 'page' });

  const handleTabUpdate = (tabUpdate: TabUpdateMessage) => {
    if (tabUpdate.updateType === 'opened') {
      setTabState(({ aliases: prevAliases, active: prevActive }) => ({
        // De-dupe and new tab aliases
        aliases: [...new Set([...prevAliases, tabUpdate.pageAlias])],
        active: tabUpdate.options?.dontSwitchToPage ? prevActive : tabUpdate.pageAlias,
      }));
      vm.sendCommand({ type: 'tab-switch', pageAlias: tabUpdate.pageAlias });
    } else if (tabUpdate.updateType === 'closed') {
      const closedTabAlias = tabUpdate.pageAlias;
      setTabState(({ aliases: prevAliases, active: prevActive }) => {
        const closedTabIndex = prevAliases.indexOf(closedTabAlias);

        const aliasesWithClosedTabRemoved = prevAliases.filter((alias) => alias !== closedTabAlias);

        const newActive =
          closedTabAlias !== prevActive ? prevActive
            // Choose the tab alias to the left of the closed tab (in most cases it's the prior tab)
            // TODO: could be improved if we keep track of the actual tab switch history
          : closedTabIndex > 0 ? prevAliases[closedTabIndex - 1]
            // If there's no left tab, choose the first tab with fallback to 'page'
          : aliasesWithClosedTabRemoved.at(0) || 'page';

        if (newActive !== prevActive) {
          vm.sendCommand({ type: 'tab-switch', pageAlias: newActive });
        }

        return { aliases: aliasesWithClosedTabRemoved, active: newActive };
      });
    } else if (tabUpdate.updateType === 'switch') {
      setTabState(({ aliases: prevAliases }) => ({
        aliases: prevAliases.includes(tabUpdate.pageAlias) ? prevAliases : [...prevAliases, tabUpdate.pageAlias],
        active: tabUpdate.pageAlias,
      }));
      // Send tab-switch command to trigger callPage.streamPage, just like we do for 'opened'
      vm.sendCommand({ type: 'tab-switch', pageAlias: tabUpdate.pageAlias });
    }
  };

  const vm = useVm({
    testContentId,
    executionType: recorderInitiator.executionType,
    startEpoch: startEpoch.current,
    onTabUpdate: handleTabUpdate,
  });

  const { recorderState } = useLiveStateManager();
  const viewPort = useMemo(() => vm.viewport ?? getDeviceViewport(testType), [testType, vm.viewport]);
  const variables = useTestVariables({
    testContentId,
    limit: recorderState?.operation ? { type: 'operationId', id: recorderState.operation.id } : undefined,
    // If we are in a valid recorder state, we can use the default scopes (hence the undefined),
    // otherwise, we dont want to show project and test variables.
    scopes: recorderState ? undefined : new Set(),
  });
  const recordType = useAtomValue(recordTypeAtom);
  const getRecorderClickMode = useAtomCallback((get) => get(recorderClickModeAtom));
  const [browserClickPosition, setBrowserClickPosition] = useAtom(browserClickPositionAtom);
  const [openDropdown, setOpenDropdown] = useAtom(contextMenuDropDownAtom);
  const [clickType, setClickType] = useState<'click' | 'dblclick'>('click');
  const clickTimeout = useRef<NodeJS.Timeout | null>(null);
  const [environment] = useAtom(currentEnvironmentAtom);

  const setRecorderTabsOpenAtom = useSetAtom(recorderTabsOpenAtom);
  useEffect(() => {
    setRecorderTabsOpenAtom(tabState.aliases.length > 1);
    return () => setRecorderTabsOpenAtom(false);
  }, [tabState.aliases]);

  const [previousClickPosition, setPreviousClickPosition] = useState<{ x: Coordinate; y: Coordinate }>({
    x: { absolute: 0, ratio: 0 },
    y: { absolute: 0, ratio: 0 },
  });

  // This is a global needed so if a user chooses to ignore the interaction warning once, it is ALWAYS ignored
  const ignoredInteractionWarning = useRef<boolean>(false);
  const valueEntryRef = useRef<HTMLInputElement>(null);

  const [showInteractionWarning, setShowInteractionWarning] = useState(false);
  const [vmMediaReady, setVmMediaReady] = useState(false);
  const [action, setAction] = useState<RecorderOperation>();

  const [mainDropdown, setMainDropdown] = useState<HTMLDivElement | null>(null);
  const [assertionDropdown] = useState<HTMLDivElement | null>(null);
  const [root, setRoot] = useState<HTMLDivElement | null>(null);
  const [valueEntryOpened, valueEntryOps] = useDisclosure(false, {
    onClose: () => {
      actionValueForm.reset();
      setAction(undefined);
      setIsPageLevelScroll(false);
    },
  });

  const [selectOptions, setSelectOptions] = useState<VmSelectOptionsPayload['options']>([]);
  const [fillInputData, setFillInputData] = useState<VmFillInputPayload['data'] | undefined>();

  const projectId = useProjectId();
  const startVmPromise = useRef<Promise<void> | null>(null);

  // Hack to refresh the native-mobile screenshot if an error occurs
  // Soemtimes we observe it turns black otherwise.
  // I assume this is due to the image proxying that we do right now, so we might be able to remove this
  // when we remove the `mjpeg.stably.ai` proxy.
  const vmImgUrl = useMemo(
    () =>
      vm.mediaSource?.type !== 'image' ?
        undefined
      : withUrlParams(vm.mediaSource.src, {
          // Flooring to restrain the resresh rate to at most 1/per 20 seconds
          t: Math.floor((Date.now() / 1000) * 20).toString(),
          vmMediaReady: vmMediaReady.toString(),
        }),
    [vm.mediaSource, vmMediaReady],
  );

  const handleClickOutside = useCallback(() => {
    if (!valueEntryOpened) {
      setBrowserClickPosition(undefined);
      setOpenDropdown(false);
    }
  }, [valueEntryOpened]);

  useClickOutside(
    () => handleClickOutside(),
    null,
    [root, mainDropdown, assertionDropdown].filter((x) => x !== null),
  );

  const handleVmStatus = useHandleVmStatus({ testContentId });
  useEffect(() => {
    if (vm.status) {
      handleVmStatus(vm.status);
    }
  }, [handleVmStatus, vm.status]);

  // Start the VM on mount.
  // On unmount, shutdown vm to save resources
  useEffect(() => {
    // Ensure startVm is only called once
    if (startVmPromise.current) {
      return;
    }
    startVmPromise.current = vm
      .startVm({
        recorderInitiatorLocation: recorderInitiator.location,
        antiBot,
        useProxy,
        environment,
      })
      .catch((e) => {
        console.error('Error starting VM:', e instanceof Error ? e.message : JSON.stringify(e));
      });
    return () => {
      vm.close({ skipSetStatus: true }).catch(() => undefined);
    };
  }, []);

  // Use the VM video in our video player
  // set a new MediaStream when the track changes (it may change if the daily participant rejoins after being disconnected due to this tab being inactive)
  useEffect(() => {
    if (videoRef.current) {
      const newSrcObject = vm.mediaSource?.type === 'video' ? new MediaStream([vm.mediaSource.track]) : null;
      videoRef.current.srcObject = newSrcObject;

      // When the media track is restored after a disconnection, the video element might be paused/suspended.
      // We need to explicitly call play() to ensure the video starts playing again.
      // This fixes the issue where the loading overlay appears after a long preview (10-15 mins)
      // because the video element fires onSuspend/onAbort during reconnection but doesn't automatically
      // start playing when the track is restored.
      if (newSrcObject) {
        videoRef.current.play().catch((e) => {
          // Ignore AbortError which can happen if play() is called while the video is already playing
          // or if the srcObject changes before play() completes
          if (e.name !== 'AbortError') {
            console.warn('Error playing video after track restoration:', e);
          }
        });
      } else {
        // Media source is unavailable, show loading overlay
        setVmMediaReady(false);
      }
    }
  }, [
    vm.mediaSource?.type,
    vm.mediaSource?.type === 'video' ? vm.mediaSource.track : undefined, // Only video type has a track
  ]);

  useEffect(() => {
    valueEntryRef.current?.focus();
  }, [valueEntryOpened]);

  // When the VM sends a 'select-options' message to the client
  useEffect(() => {
    if (!vm.selectOptionsPayload) {
      return;
    }
    const payload = vm.selectOptionsPayload;
    setBrowserClickPosition({
      x: { absolute: payload.target.x, ratio: payload.target.x / viewPort.width },
      y: { absolute: payload.target.y, ratio: payload.target.y / viewPort.height },
    });
    setAction({ method: 'selectOption', hasValue: true, level: 'element' });
    setSelectOptions(payload.options);
    valueEntryOps.open();
  }, [setBrowserClickPosition, vm.selectOptionsPayload]);

  // When the VM sends a 'fill-input' message to the client
  useEffect(() => {
    if (!vm.fillInputData) {
      return;
    }
    setFillInputData(vm.fillInputData.data);
  }, [vm.fillInputData]);

  // For when a left click is made, we want to save the action and previous click position
  useEffect(() => {
    if (browserClickPosition && !openDropdown && !valueEntryOpened) {
      saveAction({ method: recordType === 'action' ? clickType : 'toBeVisible', level: 'element' });
    }
    if (browserClickPosition) {
      setPreviousClickPosition(browserClickPosition);
    }
  }, [browserClickPosition]);

  const resetInputs = useCallback(() => {
    setBrowserClickPosition(undefined);
    setOpenDropdown(false);
    setFillInputData(undefined);
    setSelectOptions([]);
  }, []);

  useEffect(() => {
    resetInputs();
  }, [tabState.active, resetInputs]);

  const saveAction = useCallback(
    (
      action?: RecorderOperation,
      actionValue?: {
        value: string;
        valueType?: ValueType;
        screenshotType?: AIAssertionScreenshotType;
        includeScreenshot?: boolean;
        model?: AIAssertionModel;
        delay?: number;
      },
    ) => {
      if (!browserClickPosition || !action) {
        return;
      }
      const clickPosition = getClickPosition();
      if (!clickPosition) {
        console.error('Video not loaded (no height/width)');
        return;
      }

      vm.sendCommand(
        action.method === 'playwrightAction' ? { type: 'playwright-action', value: actionValue?.value || '' }
        : action.method === 'aiAction' ? { type: 'ai-step', prompt: actionValue?.value || '' }
        : {
            type: 'operation',
            clickMode: getRecorderClickMode(),
            operation:
              // Special case since we need to convert to numbers
              action.method === 'waitForTimeout' ?
                {
                  operationType: 'WAIT',
                  timeoutInMillis: Number(actionValue?.value) * 1000 || 500,
                }
              : isActionMethod(action.method) ?
                {
                  operationType: 'ACTION',
                  target: action.level === 'element' ? clickPosition : undefined,
                  // This is a hack to pass this through the value, we should have nicer types around this instead
                  target2:
                    action.method === 'dragTo' && actionValue ?
                      zPosition.parse(JSON.parse(actionValue.value))
                    : undefined,
                  value:
                    action.method === 'dragTo' ? undefined
                    : action.method === 'swipe' && actionValue?.value && isSwipeDirection(actionValue.value) ?
                      JSON.stringify({
                        x: clickPosition.x,
                        y: clickPosition.y,
                        direction: actionValue.value,
                      } satisfies z.output<typeof zSwipe>)
                    : action.method === 'scroll' ?
                      (
                        actionValue?.value // provide default value in case user submits without changing modal
                      ) ?
                        JSON.stringify(zScrollOperationValue.parse(JSON.parse(actionValue.value)))
                      : JSON.stringify(DEFAULT_SCROLL_VALUE)
                    : actionValue?.value || '',
                  valueType: actionValue?.valueType,
                  action: action.method,
                  options:
                    action.method === 'type' && actionValue?.delay ?
                      { ...action.options, delay: actionValue.delay }
                    : action.options,
                }
              : action.method === 'aiAssertion' ?
                {
                  operationType: 'AI_ASSERTION',
                  assertion: actionValue?.value || '',
                  screenshotType: actionValue?.screenshotType || 'VIEWPORT',
                  includeScreenshot: actionValue?.includeScreenshot ?? true,
                  model: actionValue?.model || 'STABLY_FLASH',
                }
              : {
                  operationType: 'ASSERTION',
                  target: action.level === 'element' ? clickPosition : undefined,
                  value: actionValue?.value || '',
                  valueType: actionValue?.valueType,
                  assertion: action.method,
                },
          },
      );
      // TODO: I might want to hear a message back from the server to know the step has been updated
      resetInputs();
    },
    [vm.sendCommand, browserClickPosition, viewPort, setBrowserClickPosition],
  );

  // Extract scroll operation value from the form, setting it to default if it's invalid,
  // so we can easily pass it to the ScrollInput component
  const scrollOperationValue = useMemo(() => {
    const parsedScroll = safeParse(actionValueForm.values.value, zScrollOperationValue);
    return parsedScroll.success ?
        parsedScroll.data
      : ({ direction: 'down', amount: 100 } satisfies z.output<typeof zScrollOperationValue>);
  }, [actionValueForm.values.value]);

  const getClickPosition = useCallback(() => {
    return viewPort && browserClickPosition ?
        ({
          xRatio: browserClickPosition.x.ratio,
          yRatio: browserClickPosition.y.ratio,
          x: viewPort.width * browserClickPosition.x.ratio,
          y: viewPort.height * browserClickPosition.y.ratio,
        } as const)
      : undefined;
  }, [viewPort, browserClickPosition]);

  const sendUploadFilesToVM = (selectedFiles: string[]) => {
    vm.sendCommand({ type: 'file-upload', fileIds: selectedFiles });
  };

  const handleShowFillInput = useCallback(() => {
    const payload = vm.fillInputData;
    if (!payload) {
      return;
    }
    setBrowserClickPosition({
      x: { absolute: payload.target.x, ratio: payload.target.x / viewPort.width },
      y: { absolute: payload.target.y, ratio: payload.target.y / viewPort.height },
    });
    setAction({ method: 'fill', hasValue: true, level: 'element' });
    actionValueForm.setFieldValue('value', payload.data.value);
    valueEntryOps.open();
  }, [vm.fillInputData, viewPort, valueEntryOps]);

  const handleShowPressInput = useCallback(() => {
    setBrowserClickPosition({
      x: { absolute: 0, ratio: 0 },
      y: { absolute: 0, ratio: 0 },
    });
    setAction({ method: 'press', hasValue: true, level: 'page' });
    valueEntryOps.open();
  }, [valueEntryOps]);

  const handleBrowserWheelEvent = useCallback(
    (e: React.WheelEvent<HTMLElement>) => {
      e.stopPropagation();

      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      const xRatio = x / rect.width;
      const yRatio = y / rect.height;

      const position = {
        x: viewPort.width * xRatio,
        y: viewPort.height * yRatio,
      };

      // Direction is the primary scrolling direction from the user's mouse wheel
      const isHorizontalScroll = Math.abs(e.deltaX) > Math.abs(e.deltaY);
      const scrollAmount = Math.abs(isHorizontalScroll ? e.deltaX : e.deltaY);
      const scrollDirection =
        isHorizontalScroll ?
          e.deltaX > 0 ?
            'right'
          : 'left'
        : e.deltaY > 0 ? 'down'
        : 'up';

      vm.sendCommand({
        type: 'scroll',
        direction: scrollDirection,
        amount: scrollAmount,
        position,
      });
    },
    [viewPort],
  );

  const handleBroswerContextMenuEvent = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      e.preventDefault();
      setOpenDropdown(true);
      if (vm.status?.state !== 'ready' && !ignoredInteractionWarning.current) {
        setShowInteractionWarning(true);
        return;
      }
      setBrowserClickPosition(getMouseEventPos(e));
    },
    [vm.status, ignoredInteractionWarning],
  );

  const handleBroswerClickEvent = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      if (vm.status?.state !== 'ready' && !ignoredInteractionWarning.current) {
        setShowInteractionWarning(true);
        return;
      }
      if (openDropdown) {
        setOpenDropdown(false);
        return;
      }
      if (clickTimeout.current) {
        clearTimeout(clickTimeout.current);
        clickTimeout.current = null;
        return;
      }
      const mouseEventPos = getMouseEventPos(e);
      clickTimeout.current = setTimeout(() => {
        setClickType('click');
        setBrowserClickPosition(mouseEventPos);
        clickTimeout.current = null;
      }, 250);
    },
    [openDropdown, vm.status, clickTimeout, ignoredInteractionWarning],
  );

  const handleBrowserDoubleClickEvent = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      if (recordType === 'assertion') {
        return;
      }
      if (vm.status?.state !== 'ready' && !ignoredInteractionWarning.current) {
        setShowInteractionWarning(true);
        return;
      }
      if (clickTimeout.current) {
        clearTimeout(clickTimeout.current);
        clickTimeout.current = null;
      }
      setClickType('dblclick');
      setBrowserClickPosition(getMouseEventPos(e));
    },
    [vm.status, clickTimeout, ignoredInteractionWarning],
  );

  const handleActionValueFormSubmission = useCallback(
    ({
      value,
      valueType,
      screenshotType,
      includeScreenshot,
      model,
      delay,
    }: {
      value: string;
      valueType: ValueType;
      screenshotType: AIAssertionScreenshotType;
      includeScreenshot: boolean;
      model: AIAssertionModel;
      delay: number;
    }) => {
      const response = { value: value.toString(), valueType };

      saveAction(action, {
        ...response,
        screenshotType: includeScreenshot ? screenshotType : 'VIEWPORT',
        includeScreenshot,
        model,
        delay,
      });
      valueEntryOps.close();
      actionValueForm.reset();
    },
    [valueEntryOps, actionValueForm, saveAction],
  );

  return (
    <>
      {valueEntryOpened && action?.method !== 'dragTo' ?
        <OverOverlay
          onClickOutsideOver={() => valueEntryOps.close()}
          zIndex={WEB_RTC_OVERLAY_ZIndex}
          useEscapeToExit={action?.method !== 'press'}
        >
          <form onSubmit={actionValueForm.onSubmit(handleActionValueFormSubmission)}>
            {action?.method === 'press' ?
              <KeyCaptureInput
                className="w-60"
                onKeyCapture={(keys) => actionValueForm.setFieldValue('value', keys.join('+'))}
                ref={valueEntryRef}
                label="Press key"
                inputProps={actionValueForm.getInputProps('value')}
              />
            : action?.method === 'swipe' ?
              <SwipeDirectionInput
                className="w-80"
                label="Swipe direction"
                value={actionValueForm.values.value as SwipeDirection}
                onDirectionSelect={(direction) => actionValueForm.setFieldValue('value', direction)}
                onSubmit={actionValueForm.onSubmit(handleActionValueFormSubmission)}
              />
            : action?.method === 'aiAssertion' ?
              <div>
                <div className="flex items-center gap-1 pb-4">AI Assert</div>
                <AIAssertionConfig
                  className="w-80"
                  assertion={actionValueForm.values.value}
                  includeScreenshot={actionValueForm.values.includeScreenshot}
                  screenshotType={actionValueForm.values.screenshotType}
                  model={actionValueForm.values.model}
                  onAssertionChange={(value) => actionValueForm.setFieldValue('value', value)}
                  onIncludeScreenshotChange={(value) => actionValueForm.setFieldValue('includeScreenshot', value)}
                  onScreenshotTypeChange={(value) => actionValueForm.setFieldValue('screenshotType', value)}
                  onModelChange={(value) => actionValueForm.setFieldValue('model', value)}
                  textareaProps={{
                    ref: valueEntryRef as unknown as React.RefObject<HTMLTextAreaElement>,
                    resize: 'vertical',
                    rows: 3,
                  }}
                />
              </div>
            : action?.method === 'aiAction' ?
              <div>
                <div className="flex items-center gap-1">
                  <IconSparkles width={15} />
                  <span className="text-sm font-medium">AI Generate</span>
                </div>
                <Textarea
                  className="mt-2 w-80"
                  ref={valueEntryRef as unknown as React.RefObject<HTMLTextAreaElement>}
                  label={<div className="mb-1 ml-1 text-sm">Describe the actions or assertions to add</div>}
                  placeholder="E.g fill out the form, verify the heading matches 'My Form'"
                  resize="vertical"
                  rows={3}
                  // Submit on Enter
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      const { form } = e.currentTarget;
                      if (form) {
                        form.requestSubmit();
                      }
                    }
                  }}
                  {...actionValueForm.getInputProps('value')}
                />
              </div>
            : action?.method === 'waitForTimeout' ?
              <NumberInput
                allowNegative={false}
                className="w-60"
                label="How many seconds to wait"
                autoFocus
                placeholder="0.5"
                {...actionValueForm.getInputProps('value')}
              />
            : action?.method === 'selectOption' ?
              <NativeSelect
                className="w-60"
                label="Select option"
                onChange={(e) => {
                  const { value } = e.currentTarget;
                  saveAction(action, { value });
                  valueEntryOps.close();
                  actionValueForm.reset();
                }}
                data={selectOptions}
              />
            : action?.method === 'scroll' ?
              <ScrollInput
                className="w-80"
                label="Add scroll"
                scrollOperationValue={scrollOperationValue}
                isPageLevelScroll={isPageLevelScroll}
                onDirectionSelect={(direction) => {
                  actionValueForm.setFieldValue(
                    'value',
                    JSON.stringify({ direction, amount: scrollOperationValue.amount }),
                  );
                }}
                onAmountChange={(amount: number) => {
                  actionValueForm.setFieldValue(
                    'value',
                    JSON.stringify({ direction: scrollOperationValue.direction, amount }),
                  );
                }}
                onIsPageLevelScroll={(isPageLevel) => {
                  setIsPageLevelScroll(isPageLevel);
                  setAction({ ...action, level: isPageLevel ? 'page' : 'element' });
                }}
              />
            : action?.method === 'playwrightAction' ?
              <MonacoInputField
                className="w-80"
                variables={variables}
                label="Playwright Action"
                labelProps={{ className: 'mb-1' }}
                autoFocus
                {...actionValueForm.getInputProps('value')}
              />
            : <div className="flex flex-col gap-2">
                <label className="text-xs">{`${capFirst(action?.method || '')} value`}</label>
                {fillInputData?.tag === 'textarea' ?
                  <ParameterizedTextInputArea
                    onChange={onParamterizedValueChange}
                    variables={variables}
                    valueType="STATIC"
                    ref={valueEntryRef as unknown as React.RefObject<HTMLTextAreaElement>}
                    placeholder={fillInputData?.placeholder}
                    className="w-80"
                    rows={3}
                  />
                : <ParameterizedInput
                    onValueChange={(x) => actionValueForm.setFieldValue('value', x)}
                    onValueTypeChange={(x) => actionValueForm.setFieldValue('valueType', x)}
                    value={actionValueForm.values.value}
                    valueType={actionValueForm.values.valueType}
                    variables={variables}
                    ref={valueEntryRef}
                    className="w-80"
                    placeholder={fillInputData?.placeholder}
                    type={fillInputData?.type}
                    autoFocus
                  />
                }
                {action?.method === 'type' && (
                  <NumberInput
                    label="Delay between characters"
                    className="w-80"
                    suffix="ms"
                    min={0}
                    max={500}
                    step={50}
                    allowNegative={false}
                    placeholder={`${DEFAULT_DELAY_MS}ms`}
                    defaultValue={DEFAULT_DELAY_MS}
                    {...actionValueForm.getInputProps('delay')}
                  />
                )}
              </div>
            }
            {action?.method !== 'selectOption' && action?.method !== 'swipe' && (
              <div className="flex justify-end">
                <Button
                  className="mt-4"
                  type="submit"
                  size="xs"
                  rightSection={<IconCornerDownLeft size={12} />}
                  disabled={!actionValueForm.isValid()}
                >
                  Enter
                </Button>
              </div>
            )}
          </form>
        </OverOverlay>
      : null}
      <Browser
        testType={testType}
        executionType={recorderInitiator.executionType}
        epochId={startEpoch.current}
        sendCommand={vm.sendCommand}
        aiActionButton={() => {
          setBrowserClickPosition({ x: { absolute: 0, ratio: 0 }, y: { absolute: 0, ratio: 0 } });
          setAction({ method: recordType === 'action' ? 'aiAction' : 'aiAssertion', level: 'page', hasValue: true });
          valueEntryOps.open();
        }}
        onKeyDown={(e) => {
          // Prevent this on opening with shift key since that's used for horizontal scrolling
          if (testType !== 'MOBILE_APP' && vm.status?.state === 'ready' && !valueEntryOpened && !e.shiftKey) {
            if (fillInputData) {
              handleShowFillInput();
            } else {
              handleShowPressInput();
            }
          }
        }}
        onBrowserHover={(isHovered, browserElement) => {
          if (isHovered && !valueEntryOpened) {
            const timeoutId = setTimeout(() => browserElement.focus(), 100);
            return () => clearTimeout(timeoutId);
          } else if (!isHovered) {
            browserElement.blur();
          }
        }}
        className={recordType === 'assertion' ? 'cursor-crosshair' : ''}
        activeTab={vm.activeTab}
      >
        {tabState.aliases.length > 1 && (
          <Tabs
            className="bg-neutral-50 dark:bg-neutral-600"
            variant="pills"
            radius="none"
            value={tabState.active}
            onChange={(pageAlias) => {
              if (!pageAlias) {
                return;
              }
              setTabState(({ aliases: prevAliases }) => ({ aliases: prevAliases, active: pageAlias }));
              vm.sendCommand({ type: 'tab-switch', pageAlias });
            }}
          >
            <Tabs.List className="gap-0">
              {tabState.aliases.map((tabAlias) => (
                <Tabs.Tab
                  key={tabAlias}
                  value={tabAlias}
                  className="h-5"
                  style={{ fontSize: '0.75em' }}
                >
                  {tabAlias}
                </Tabs.Tab>
              ))}
            </Tabs.List>
          </Tabs>
        )}
        <div
          className="relative h-full w-full"
          ref={setRoot}
        >
          {valueEntryOpened && action?.method === 'dragTo' && browserClickPosition ?
            <DragToOverlay
              onClickOutsideOver={() => valueEntryOps.close()}
              zIndex={WEB_RTC_OVERLAY_ZIndex}
              initPos={{ x: browserClickPosition.x.absolute, y: browserClickPosition.y.absolute }}
              videoSize={videoRef.current?.getBoundingClientRect()}
              onInsideClick={async (mousePos) => {
                if (!videoRef.current) {
                  console.error('Video not loaded (no height/width)');
                  valueEntryOps.close();
                } else {
                  const rect = videoRef.current.getBoundingClientRect();
                  const mousePosRatio = {
                    x: mousePos.x / rect.width,
                    y: mousePos.y / rect.height,
                  };

                  const mouseVideoPos = {
                    x: viewPort.width * mousePosRatio.x,
                    y: viewPort.height * mousePosRatio.y,
                  };

                  saveAction(action, { value: JSON.stringify({ x: mouseVideoPos.x, y: mouseVideoPos.y }) });
                  valueEntryOps.close();
                }
              }}
              onContextClick={(e) => {
                e.preventDefault();
                handleClickOutside();
                valueEntryOps.close();
              }}
            />
          : null}
          {showInteractionWarning && vm.status?.state !== 'ready' ?
            <OverOverlay zIndex={WEB_RTC_OVERLAY_ZIndex}>
              <div className="flex flex-col items-center gap-2">
                The VM is not yet ready.
                <div className="flex flex-row gap-2">
                  <Button
                    variant="outline"
                    onClick={() => {
                      ignoredInteractionWarning.current = true;
                      setShowInteractionWarning(false);
                    }}
                  >
                    Ignore Warning
                  </Button>
                  <Button
                    onClick={() => {
                      ignoredInteractionWarning.current = true;
                      setShowInteractionWarning(false);
                    }}
                  >
                    Okay
                  </Button>
                </div>
              </div>
            </OverOverlay>
          : !vmMediaReady ?
            <LoadingOverlay
              loaderProps={{ children: <BrowserLoader testType={testType} /> }}
              visible={true}
            />
          : null}

          <FileChooser
            projectId={projectId!}
            open={vm.fileDialogIsOpen}
            onFileSelect={(files) => sendUploadFilesToVM(files.map((f) => f.id))}
            onClose={() => sendUploadFilesToVM([])}
          />

          <div
            className="relative h-full"
            id={TOUR_ID.BROWSER_VM}
          >
            {openDropdown && browserClickPosition && !valueEntryOpened ?
              <BrowserContextMenu
                zIndex={WEB_RTC_OVERLAY_ZIndex}
                browserClickPosition={browserClickPosition}
                valueEntryOpen={valueEntryOps.open}
                setMainDropdown={setMainDropdown}
                setAction={setAction}
                saveAction={saveAction}
                setFillInputData={setFillInputData}
                testType={testType}
              />
            : null}

            {vm.mediaSource?.type === 'image' ?
              // oxlint-disable-next-line @next/next/no-img-element
              <img
                src={vmImgUrl}
                alt="Browser VM"
                className={clsx('w-full', vmImgUrl ? 'opacity-100' : 'opacity-0')}
                draggable={false}
                onLoad={() => setVmMediaReady(true)}
                onError={() => setVmMediaReady(false)}
                onWheel={(e) => handleBrowserWheelEvent(e)}
                onContextMenu={(e) => handleBroswerContextMenuEvent(e)}
                onClick={(e) => handleBroswerClickEvent(e)}
                onDoubleClick={(e) => handleBrowserDoubleClickEvent(e)}
              />
            : <video
                aria-label="Browser VM"
                ref={videoRef}
                autoPlay
                playsInline
                className="w-full"
                onPlaying={() => setVmMediaReady(true)}
                onAbort={() => {
                  // Don't immediately show loading overlay on abort.
                  // The useEffect watching vm.mediaSource will handle showing the
                  // loading overlay when the media source becomes unavailable.
                }}
                onSuspend={() => {
                  // The video can be suspended when:
                  // 1. The tab is inactive
                  // 2. The connection is temporarily disrupted
                  // 3. The video has finished loading all available data
                  //
                  // Don't immediately show the loading overlay - the video will naturally
                  // recover and fire onPlaying when frames resume. The loading overlay
                  // is only shown when the media source becomes unavailable (handled in
                  // the useEffect that watches vm.mediaSource).
                  //
                  // Try to resume playback to help recover from temporary suspensions.
                  if (vm.mediaSource && videoRef.current) {
                    videoRef.current.play().catch(() => {
                      // Ignore play errors - if the media source is truly gone,
                      // the useEffect will handle showing the loading overlay
                    });
                  }
                }}
                onWheel={(e) => handleBrowserWheelEvent(e)}
                onContextMenu={(e) => handleBroswerContextMenuEvent(e)}
                onClick={(e) => handleBroswerClickEvent(e)}
                onDoubleClick={(e) => handleBrowserDoubleClickEvent(e)}
              />
            }
            {fillInputData ?
              <ActionIcon
                className="animate-fadeIn absolute left-1 top-1 flex gap-1 opacity-80 group-focus:opacity-100"
                color="gray"
                variant="filled"
                size="md"
                onClick={handleShowFillInput}
                title="Open fill input"
              >
                <IconForms size={16} />
              </ActionIcon>
            : null}
            {!valueEntryOpened ?
              <div
                key={JSON.stringify(previousClickPosition)}
                style={{
                  zIndex: WEB_RTC_OVERLAY_ZIndex + 1,
                  left: previousClickPosition.x.absolute,
                  top: previousClickPosition.y.absolute,
                }}
                className="animate-fadeOut pointer-events-none absolute h-2 w-2 -translate-x-1 -translate-y-1 rounded-full border-2 border-[#334155] bg-slate-200 opacity-0"
              />
            : null}
          </div>
        </div>
      </Browser>
    </>
  );
}
